import {
  GraduationCapIcon,
  BadgeCheckIcon,
  SparklesIcon,
  LanguagesIcon,
  CodeIcon,
  FolderGitIcon,
  ExternalLinkIcon,
  ClipboardCheckIcon,
} from 'lucide-react';
import type { Customer, Education, Certification, Project, EntranceExam } from '@repo/types';
import { Badge } from '@/components/ui/badge';
import { SectionCard, FieldTile, TagList, EmptyState, TimelineItem } from './_shared';
import { humanize } from './utils';

interface CodingProfile {
  label: string;
  rank?: string;
  url?: string;
}

export function CustomerEducationTab({ customer }: { customer: Customer }) {
  const coding: CodingProfile[] = (customer.rankingPlatforms ?? []).map((p) => ({
    label: p.name,
    rank: p.rank || undefined,
    url: p.profile_link || undefined,
  }));

  return (
    <div className="space-y-5">
      <div className="space-y-5">
        {/* Education */}
        <SectionCard
          icon={GraduationCapIcon}
          title="Education"
          description={
            customer.highestEducationLevel ? `Highest: ${humanize(customer.highestEducationLevel)}` : undefined
          }
        >
          {customer.educationHistory?.length ? (
            <div className="space-y-3">
              {customer.educationHistory.map((edu: Education, idx) => (
                <TimelineItem
                  key={idx}
                  title={[edu.degree, edu.branch].filter(Boolean).join(' in ') || humanize(edu.education_level)}
                  subtitle={edu.institution_name}
                  meta={
                    <>
                      {edu.education_level && <span>{humanize(edu.education_level)}</span>}
                      {edu.year_of_passing && <span>Graduated {edu.year_of_passing}</span>}
                      {(edu.percentage != null || edu.grade) && (
                        <span>{edu.percentage != null ? `${edu.percentage}%` : edu.grade}</span>
                      )}
                      {edu.status && <span>{humanize(edu.status)}</span>}
                    </>
                  }
                />
              ))}
            </div>
          ) : (
            <EmptyState icon={GraduationCapIcon} message="No education records" />
          )}
        </SectionCard>

        {/* Certifications */}
        <SectionCard
          icon={BadgeCheckIcon}
          title="Certifications"
          description={customer.certifications?.length ? `${customer.certifications.length} total` : undefined}
        >
          {customer.certifications?.length ? (
            <div className="space-y-3">
              {customer.certifications.map((cert: Certification, idx) => (
                <TimelineItem
                  key={idx}
                  title={cert.title}
                  subtitle={cert.organization}
                  meta={cert.issued_date ? <span>Issued {cert.issued_date}</span> : undefined}
                >
                  {cert.description && <p className="text-xs text-muted-foreground">{cert.description}</p>}
                </TimelineItem>
              ))}
            </div>
          ) : (
            <EmptyState icon={BadgeCheckIcon} message="No certifications" />
          )}
        </SectionCard>
      </div>

      {/* Entrance exams */}
      {customer.entranceExams?.length ? (
        <SectionCard
          icon={ClipboardCheckIcon}
          title="Entrance Exams"
          description={`${customer.entranceExams.length} total`}
        >
          <div className="space-y-3">
            {customer.entranceExams.map((exam: EntranceExam, idx) => (
              <TimelineItem
                key={idx}
                title={exam.name}
                meta={
                  <>
                    {exam.cleared_year && <span>Cleared {exam.cleared_year}</span>}
                    {exam.score && <span>Score: {exam.score}</span>}
                    {exam.national_rank && <span>National Rank: {exam.national_rank}</span>}
                    {exam.state_rank && <span>State Rank: {exam.state_rank}</span>}
                  </>
                }
              />
            ))}
          </div>
        </SectionCard>
      ) : null}

      {/* Skills + Languages */}
      <SectionCard icon={SparklesIcon} title="Skills & Languages">
        {customer.skills?.length ||
        customer.softSkills?.length ||
        customer.languageKnown?.length ||
        customer.languageCertifications?.length ? (
          <div className="space-y-4">
            {customer.skills?.length ? (
              <TagList label="Technical Skills" items={customer.skills.map((s) => (s.proficiency ? `${s.name} · ${s.proficiency}` : s.name))} />
            ) : null}
            {customer.softSkills?.length ? (
              <TagList label="Soft Skills" items={customer.softSkills.map((s) => s.name)} />
            ) : null}
            {customer.languageKnown?.length ? (
              <div className="space-y-2">
                <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  <LanguagesIcon className="mr-1 inline h-3 w-3" aria-hidden />
                  Languages
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {customer.languageKnown.map((lang, i) => (
                    <Badge key={i} variant="outline" className="font-normal">
                      {lang.language_name}
                      {(() => {
                        const abilities = [
                          lang.can_read && 'Read',
                          lang.can_write && 'Write',
                          lang.can_speak && 'Speak',
                        ].filter(Boolean);
                        return abilities.length ? ` · ${abilities.join('/')}` : '';
                      })()}
                    </Badge>
                  ))}
                </div>
              </div>
            ) : null}
            {customer.languageCertifications?.length ? (
              <TagList
                label="Language Certifications"
                items={customer.languageCertifications.map((c) =>
                  c.level ? `${c.name} · ${c.level}` : c.name,
                )}
              />
            ) : null}
          </div>
        ) : (
          <EmptyState icon={SparklesIcon} message="No skills or languages on file" />
        )}
      </SectionCard>

      {/* Coding profiles */}
      {coding.length > 0 && (
        <SectionCard icon={CodeIcon} title="Competitive Coding">
          <div className="grid gap-3 sm:grid-cols-3">
            {coding.map((c, i) => (
              <FieldTile key={i} label={c.label}>
                <div className="flex items-center justify-between gap-2">
                  {c.url ? (
                    <a
                      href={c.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
                    >
                      View Profile
                      <ExternalLinkIcon className="h-3 w-3" aria-hidden />
                    </a>
                  ) : (
                    <span className="text-sm text-muted-foreground">—</span>
                  )}
                  {c.rank && (
                    <Badge variant="secondary" className="font-mono text-xs">
                      {c.rank}
                    </Badge>
                  )}
                </div>
              </FieldTile>
            ))}
          </div>
        </SectionCard>
      )}

      {/* Projects */}
      <SectionCard
        icon={FolderGitIcon}
        title="Projects"
        description={customer.projects?.length ? `${customer.projects.length} total` : undefined}
      >
        {customer.projects?.length ? (
          <div className="space-y-3">
            {customer.projects.map((project: Project, idx) => (
              <TimelineItem
                key={idx}
                title={project.title}
                meta={
                  project.start_date || project.end_date ? (
                    <span>
                      {project.start_date} — {project.end_date || 'Present'}
                    </span>
                  ) : undefined
                }
              >
                {project.description && <p className="text-xs text-muted-foreground">{project.description}</p>}
                <TagList label="Technologies" items={project.technologies_used ?? []} />
                <TagList label="Skills Learned" items={project.skills ?? []} tone="success" />
                <div className="flex flex-wrap gap-3">
                  {project.live_link && (
                    <a
                      href={project.live_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                    >
                      Live <ExternalLinkIcon className="h-3 w-3" aria-hidden />
                    </a>
                  )}
                </div>
              </TimelineItem>
            ))}
          </div>
        ) : (
          <EmptyState icon={FolderGitIcon} message="No projects" />
        )}
      </SectionCard>
    </div>
  );
}
